/**
 * Public surface of the shared symbol-sprite module.
 *
 * Both `UnifiedAnchorSymbolLayer` and `NoteSymbolLayer` consume sprites from
 * here so a single registration pass populates the MapLibre image atlas.
 */

import type { Map as MaplibreMap } from 'maplibre-gl';
import { generateAllSprites, type SpriteEntry } from './spriteGenerator';

export {
  SHAPE_SPRITE_SIZE,
  SHAPE_TYPES,
  NOTE_PIN_SPRITE_KEY,
  NOTE_ICON_SPRITE_KEY,
  compositeKey,
  generateShapeSprite,
  generateAllSprites,
  type SpriteEntry,
} from './spriteGenerator';
export { generateObstructionSprites } from './obstructionSprites';

// ===== SHARED SPRITE CACHE =====

/**
 * Module-scoped cache. The first layer to mount kicks off generation; every
 * subsequent caller awaits the same Promise — guaranteeing one canvas pass and
 * one atlas registration regardless of how many symbol layers mount.
 */
let spriteCachePromise: Promise<Map<string, SpriteEntry>> | null = null;

export function getSpriteCache(): Promise<Map<string, SpriteEntry>> {
  if (!spriteCachePromise) {
    const pending = generateAllSprites();
    // Reset on rejection so the next consumer can retry — without this a
    // single transient SVG-load failure would brick every future layer mount.
    pending.catch(() => {
      if (spriteCachePromise === pending) spriteCachePromise = null;
    });
    spriteCachePromise = pending;
  }
  return spriteCachePromise;
}

/**
 * Adds every sprite in the shared cache to the supplied MapLibre map. No-op
 * for sprites already registered (idempotent across repeated layer mounts and
 * style.load reruns).
 */
export function registerSpritesOnMap(
  map: MaplibreMap,
  sprites: Map<string, SpriteEntry>
): void {
  for (const [imageId, entry] of sprites) {
    if (map.hasImage(imageId)) continue;
    map.addImage(
      imageId,
      {
        width: entry.data.width,
        height: entry.data.height,
        data: new Uint8Array(entry.data.data.buffer),
      },
      { sdf: entry.sdf, pixelRatio: entry.pixelRatio ?? 1 }
    );
  }
}
